import { useRef, useState, type DragEvent } from 'react';
import clsx from 'clsx';
import { FileText, Upload, Wand2, X } from '@/components/icons';
import { REQUIREMENT_FILE_ACCEPT, REQUIREMENT_FILE_TYPES, REQUIREMENT_LIMITS } from '@1qa/shared';
import { formatBytes } from '@/lib/format';
import { filesForUpload, nameFromFile } from '@/lib/requirements';
import { Button, Card, Field, Input, Textarea } from '@/components/ui';

export interface UploadInput {
  name: string;
  files: { name: string; data: string }[];
  text?: string;
  notes?: string;
}

/** Step 1: the business documents (files and/or pasted text), a name and notes for the AI model. */
export function UploadForm({ busy, onSubmit }: { busy: boolean; onSubmit: (input: UploadInput) => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [text, setText] = useState('');
  const [notes, setNotes] = useState('');
  const [name, setName] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const add = (list: FileList | null) => {
    if (!list) return;
    setProblem(null);
    const next = [...files];
    for (const f of Array.from(list)) {
      const ok = REQUIREMENT_FILE_TYPES.some((t) => f.name.toLowerCase().endsWith(t));
      if (!ok) {
        setProblem(f.name.toLowerCase().endsWith('.doc') ? `${f.name}: old Word files (.doc) can't be read. Save it as .docx or PDF.` : `${f.name}: use PDF, Word (.docx), text or Markdown files.`);
        continue;
      }
      if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
    }
    if (next.length > REQUIREMENT_LIMITS.files) setProblem(`Upload at most ${REQUIREMENT_LIMITS.files} documents at a time.`);
    setFiles(next.slice(0, REQUIREMENT_LIMITS.files));
    if (!name && next[0]) setName(nameFromFile(next[0].name));
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    add(e.dataTransfer.files);
  };

  const submit = async () => {
    setProblem(null);
    try {
      const encoded = await filesForUpload(files);
      onSubmit({
        name: name.trim() || (files[0] ? nameFromFile(files[0].name) : 'Requirements'),
        files: encoded,
        ...(text.trim() ? { text } : {}),
        ...(notes.trim() ? { notes } : {}),
      });
    } catch (err) {
      setProblem((err as Error).message);
    }
  };

  const ready = files.length > 0 || text.trim().length > 0;

  return (
    <Card className="space-y-6 p-6">
      <div>
        <h2 className="text-lg font-semibold text-fg">1. Add the business documents</h2>
        <p className="mt-1 text-sm text-fg-secondary">
          A BRD, user stories, a functional specification or acceptance criteria. 1QA reads the text, the project’s AI model lists the
          requirements, and you review everything before any test is saved.
        </p>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        className={clsx(
          'flex flex-col items-center justify-center gap-3 rounded-card border-2 border-dashed px-6 py-10 text-center transition',
          over ? 'border-primary bg-primary-subtle/40' : 'border-border',
        )}
      >
        <Upload className="size-6 text-primary" aria-hidden />
        <p className="text-sm text-fg">
          Drop files here or{' '}
          <button type="button" className="font-semibold text-link hover:underline" onClick={() => input.current?.click()}>
            choose files
          </button>
        </p>
        <p className="text-xs text-fg-muted">
          PDF, Word (.docx), text or Markdown · up to {REQUIREMENT_LIMITS.files} files, {REQUIREMENT_LIMITS.fileBytes / 1024 / 1024} MB each · text only
          (images and scanned pages are not read)
        </p>
        <input
          ref={input}
          type="file"
          multiple
          accept={REQUIREMENT_FILE_ACCEPT}
          className="hidden"
          data-testid="requirements-file-input"
          onChange={(e) => {
            add(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {files.length > 0 && (
        <ul className="divide-y divide-border-subtle rounded-card ring-1 ring-border ring-inset">
          {files.map((f) => (
            <li key={`${f.name}-${f.size}`} className="flex items-center gap-3 px-4 py-3 text-sm">
              <FileText className="size-4 text-fg-muted" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-fg">{f.name}</span>
              <span className="text-xs text-fg-muted">{formatBytes(f.size)}</span>
              <button type="button" aria-label={`Remove ${f.name}`} className="text-fg-muted hover:text-fg" onClick={() => setFiles(files.filter((x) => x !== f))}>
                <X className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Field label="Or paste the requirements" hint="User stories from a ticket, an email from the business team, acceptance criteria…">
        {(id) => <Textarea id={id} rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder="As a customer I want to…" />}
      </Field>

      <div className="grid gap-6 md:grid-cols-2">
        <Field label="Name" hint="Shown in the list of uploads and on the tests made from it.">
          {(id) => <Input id={id} value={name} maxLength={200} onChange={(e) => setName(e.target.value)} placeholder="Checkout BRD v2" />}
        </Field>
        <Field label="Notes for the AI model (optional)" hint="Roles and test accounts, which parts to leave out, words the business uses.">
          {(id) => <Textarea id={id} rows={3} value={notes} maxLength={4000} onChange={(e) => setNotes(e.target.value)} placeholder="Only the customer app; admins are out of scope." />}
        </Field>
      </div>

      {problem && (
        <p role="alert" className="rounded-field bg-warning-subtle p-3 text-sm text-fg ring-1 ring-warning/40 ring-inset">
          {problem}
        </p>
      )}

      <div className="flex justify-end">
        <Button icon={<Wand2 className="size-4" />} loading={busy} disabled={!ready} onClick={submit} data-testid="requirements-upload">
          Upload and find requirements
        </Button>
      </div>
    </Card>
  );
}
